import type { ShopperProducts } from '@/scapi';
import { isCategoryPlpPathname, normalizeCategoryPath } from '@/lib/category-url';

type Category = ShopperProducts.schemas['Category'];

export type RouteMatchWithPrimaryCategoryPath = {
    data?: {
        primaryCategoryPath?: string | null;
    };
};

/** Finds the L1 category whose `c_url` matches the stored primary category path. */
export function findInitialL1Category(categories: Category[], primaryPath: string | null | undefined): Category | null {
    const normalizedPrimary = normalizeCategoryPath(primaryPath);
    if (!normalizedPrimary) {
        return null;
    }

    return (
        categories.find((category) => {
            const normalizedCategoryUrl = normalizeCategoryPath(String(category.c_url ?? ''));
            if (!normalizedCategoryUrl) {
                return false;
            }

            return (
                normalizedPrimary === normalizedCategoryUrl || normalizedPrimary.startsWith(`${normalizedCategoryUrl}/`)
            );
        }) ?? null
    );
}

/**
 * Resolves the best available primary category path (catalog L1 or editorial pillar root, e.g. `/ristoranti`)
 * for the header home link and catalog menu pre-selection.
 * Priority: active route loader data → current PLP pathname → layout loader cookie read.
 */
export function resolveEffectivePrimaryCategoryPath(options: {
    layoutPath?: string | null;
    pathname: string;
    matches: RouteMatchWithPrimaryCategoryPath[];
}): string | null {
    const { layoutPath, pathname, matches } = options;

    for (let index = matches.length - 1; index >= 0; index -= 1) {
        const routePath = normalizeCategoryPath(matches[index]?.data?.primaryCategoryPath);
        if (routePath) {
            return routePath;
        }
    }

    const pathnamePrimary = normalizeCategoryPath(pathname);
    if (pathnamePrimary && isCategoryPlpPathname(pathnamePrimary)) {
        return pathnamePrimary;
    }

    return normalizeCategoryPath(layoutPath);
}
